import axios from 'axios'

const API_BASE_URL = 'https://template-ai-api.propnex.net'
// const API_BASE_URL = 'http://localhost:8000'

// Create axios instance with extended timeout for AI requests
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000, // 5 minutes (300 seconds) for AI model processing
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'User-Agent': 'AI-Model-Tester/1.0',
    },
    // Ensure proper HTTP/1.1 formatting
    httpAgent: false,
    httpsAgent: false,
    maxRedirects: 5,
    validateStatus: function (status) {
        return status >= 200 && status < 500 // Accept 4xx errors to handle them properly
    }
})

// Request interceptor for logging and validation
apiClient.interceptors.request.use(
    (config) => {
        const startTime = Date.now()
        config.metadata = { startTime }

        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`)
        console.log(`⏱️ Timeout set to: ${config.timeout}ms (${config.timeout / 1000}s)`)
        console.log(`📋 Headers:`, config.headers)
        console.log(`📦 Data:`, config.data)

        // Ensure data is properly serialized
        if (config.data && typeof config.data === 'object') {
            config.data = JSON.stringify(config.data)
        }

        return config
    },
    (error) => {
        console.error('❌ API Request Error:', error)
        return Promise.reject(error)
    }
)

// Response interceptor for logging with timing
apiClient.interceptors.response.use(
    (response) => {
        const endTime = Date.now()
        const duration = endTime - (response.config.metadata?.startTime || endTime)

        console.log(`✅ API Response: ${response.status} ${response.config.url}`)
        console.log(`⏱️ Request duration: ${duration}ms (${(duration / 1000).toFixed(2)}s)`)
        console.log(`📨 Response Data:`, response.data)
        return response
    },
    (error) => {
        const endTime = Date.now()
        const duration = endTime - (error.config?.metadata?.startTime || endTime)

        console.error(`❌ API Response Error: ${error.response?.status || 'NO_RESPONSE'} ${error.message}`)
        console.error(`⏱️ Request duration before error: ${duration}ms (${(duration / 1000).toFixed(2)}s)`)

        if (error.code === 'ECONNABORTED') {
            console.error(`🕐 TIMEOUT: Request exceeded ${error.config?.timeout || 'unknown'}ms`)
        }

        if (error.response?.data) {
            console.error('📨 Error Response Data:', error.response.data)
        }
        return Promise.reject(error)
    }
)

/**
 * Test a specific AI model with configurable timeout
 * @param {Object} formData - Form data from the UI
 * @param {string} modelId - Model ID to test
 * @param {number} customTimeout - Custom timeout in milliseconds (optional)
 * @returns {Promise} API response
 */
export const testModel = async (formData, modelId, customTimeout = null) => {
    try {
        // Validate input data
        if (!formData || !modelId) {
            throw new Error('Missing required parameters: formData or modelId')
        }

        // Clean and validate form data
        const cleanFormData = {
            project_id: String(formData.project_id || '201').trim(),
            custom_prompt: String(formData.custom_prompt || '').trim(),
            mood_and_tone: String(formData.mood_and_tone || 'Professional').trim(),
            target_audiences: String(formData.target_audiences || 'Investors').trim(),
            model_settings: {
                model: String(modelId).trim()
            },
            gpt_or_gemini: modelId, // Pass the full model name
            include_costs: true     // Request cost information
        }

        // Determine timeout based on model
        let timeout = customTimeout || 300000 // Default 5 minutes

        // Adjust timeout based on model type
        if (modelId.includes('gpt-4.1-nano')) {
            timeout = 300000 // 5 minutes for GPT-4.1 Nano
        } else if (modelId.includes('gemini-2.5-flash-lite')) {
            timeout = 180000 // 3 minutes for Gemini Flash Lite (fastest)
        } else if (modelId.includes('gemini-2.5-flash')) {
            timeout = 240000 // 4 minutes for Gemini Flash
        } else if (modelId.includes('gemini-2.5-pro')) {
            timeout = 360000 // 6 minutes for Gemini Pro (more complex)
        } else {
            timeout = 300000 // 5 minutes default
        }

        console.log(`🔧 Testing model: ${modelId}`)
        console.log(`⏱️ Using timeout: ${timeout}ms (${timeout / 1000}s)`)
        console.log('📋 Cleaned request body:', cleanFormData)

        // Make the API request with custom timeout
        const response = await apiClient({
            method: 'POST',
            url: '/leads_gen_theme',
            data: cleanFormData,
            timeout: timeout,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        })

        console.log(`✅ Model ${modelId} responded successfully`)

        // Log cost info if available
        if (response.data?.cost_info) {
            console.log(`💰 Cost for ${modelId}: $${response.data.cost_info.total_cost}`)
        }

        // Validate response
        if (!response.data) {
            throw new Error('Empty response from server')
        }

        return response.data

    } catch (error) {
        // ... existing error handling remains the same
        console.error(`❌ Model ${modelId} failed:`, error.message)

        if (error.code === 'ECONNABORTED') {
            const timeoutSeconds = (error.config?.timeout || 0) / 1000
            throw new Error(`Request timeout after ${timeoutSeconds}s. Model ${modelId} is taking longer than expected to respond.`)
        } else if (error.response) {
            const status = error.response.status
            const statusText = error.response.statusText || 'Server Error'
            const errorData = error.response.data

            console.error(`📨 Server Error Response:`, errorData)

            if (status === 400) {
                throw new Error(`Bad Request: Invalid data sent to server. Check your form inputs.`)
            } else if (status === 401) {
                throw new Error(`Unauthorized: API key or authentication required.`)
            } else if (status === 403) {
                throw new Error(`Forbidden: Access denied to API endpoint.`)
            } else if (status === 404) {
                throw new Error(`Not Found: API endpoint does not exist.`)
            } else if (status === 422) {
                throw new Error(`Validation Error: ${errorData?.message || 'Invalid request data'}`)
            } else if (status === 429) {
                throw new Error(`Rate Limited: Too many requests. Please wait and try again.`)
            } else if (status === 504) {
                throw new Error(`Gateway Timeout: Server took too long to process the request. Try again with a smaller prompt.`)
            } else if (status >= 500) {
                throw new Error(`Server Error (${status}): ${statusText}`)
            } else {
                throw new Error(`HTTP ${status}: ${statusText}`)
            }
        } else if (error.request) {
            console.error('📡 No response received:', error.request)
            throw new Error('No response from server. Check your internet connection and API endpoint.')
        } else {
            throw new Error(error.message || 'Unknown error occurred while testing model')
        }
    }
}

/**
 * Test API connectivity with quick timeout
 * @returns {Promise<boolean>} True if API is reachable
 */
export const testAPIConnectivity = async () => {
    try {
        console.log('🔍 Testing API connectivity...')

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout for connectivity test

        const response = await fetch(API_BASE_URL + '/leads_gen_theme', {
            method: 'OPTIONS',
            headers: {
                'Content-Type': 'application/json',
            },
            signal: controller.signal
        })

        clearTimeout(timeoutId)
        console.log(`✅ Connectivity test result: ${response.status}`)
        return response.status < 500
    } catch (error) {
        if (error.name === 'AbortError') {
            console.error('❌ Connectivity test timeout after 10s')
        } else {
            console.error('❌ API Connectivity Test Failed:', error)
        }
        return false
    }
}

/**
 * Get available models with their configurations
 * @returns {Array} Array of model configurations
 */
export const getAvailableModels = () => {
    return [
        // OpenAI Models
        // {
        //     id: "gpt-4.1-nano",
        //     name: "GPT-4.1 Nano",
        //     description: "🏆 Best choice - fastest speed, lowest cost, premium quality",
        //     costPerRequest: "$0.000159",
        //     speed: "7.25 req/s",
        //     recommended: true,
        //     provider: "OpenAI"
        // },
        // Google Gemini Models
        {
            id: "gemini-2.5-flash-lite",
            name: "Gemini 2.5 Flash Lite",
            description: "⚡ Ultra-fast and cost-effective for simple tasks",
            costPerRequest: "$0.000075",
            speed: "12.5 req/s",
            recommended: true,
            provider: "Google"
        },
        {
            id: "gemini-2.5-flash-lite",
            name: "Gemini 2.5 Flash Lite",
            description: "⚡ Ultra-fast and cost-effective for simple tasks",
            costPerRequest: "$0.000075",
            speed: "12.5 req/s",
            recommended: true,
            provider: "Google"
        }
    ]
}